import { Col, Row, Space } from "antd";
import { DeleteWithConfirmationButton } from "components/SeasonView/components/DeleteWithConfirmationButton";
import { EditModeToggle } from "components/Shared/EditModeToggle";
import { RosterAndStaffTable } from "components/Shared/RosterAndStaffTable";
import { SearchInput } from "components/Shared/SearchInput";
import { PeopleTypes } from "enums/peopleTypes";
import { useRecordsState } from "hooks/useRecordsState";
import { useAddSeasonRosterPlayers, useDeleteSeasonRosterPlayers } from "hooks/useSeasonRosterApi";
import { useUpdateSeasonRosterPlayer } from "hooks/useUpdateRosterPlayer";
import { useState } from "react";
import { useIntl } from "react-intl";
import { useParams } from "react-router-dom";
import { filterPersonList } from "utils/filterPersonList";
import { getPersonDeleteApiData } from "utils/getPersonDeleteApiData";
import { getPersonRecordKey } from "utils/getPersonRecordKey";
import { runAsyncAction } from "utils/runAsyncAction";
import { useAddPeopleRosterColumns } from "../hooks/useAddPeopleRosterColumns";
import { useSeasonRosterColumns } from "../hooks/useSeasonRosterColumns";
import { AddPeople } from "./AddPeople";
import { CopyPeople } from "./CopyPeople";
import UploadPersonImageModal from "./Images/UploadPersonImageModal";

type Props = {
  isLoading: boolean;
  season: any;
  roster: Array<any>;
  viewOnly: boolean;
};

export const SeasonRosterTab = ({ isLoading, season, roster, viewOnly }: Props) => {
  const { formatMessage } = useIntl();
  const { entityId: entityIdFromParams, seasonId: seasonIdFromParams } = useParams();
  const entityId = entityIdFromParams || "undefined";
  const seasonId = seasonIdFromParams || "undefined";
  const [searchText, setSearchText] = useState("");
  const [editMode, setEditMode] = useState(false);
  const [imageUploadPerson, setImageUploadPerson] = useState<object>();
  const [selectedRowRecords, setSelectedRowRecords] = useState([]);
  const filteredRoster = filterPersonList(roster, searchText);
  const selectedRowKeys = selectedRowRecords.map(getPersonRecordKey);

  const { mutateAsync: addRecordsApi, isPending: areRecordsAdding } = useAddSeasonRosterPlayers();
  const { mutateAsync: deleteRecordsApi, isPending: areRecordsDeleting } = useDeleteSeasonRosterPlayers();
  const { mutateAsync: updateRecord, updatingRecord, isUpdating: isRecordBeingUpdated } = useUpdateSeasonRosterPlayer();

  const { checkIfRecordBeingBusy, ensureRecordsBusy } = useRecordsState({
    isAddingOrDeleting: areRecordsAdding || areRecordsDeleting,
    isUpdating: isRecordBeingUpdated,
    updatingRecord,
    applyForAll: !season?.taskStatus,
  });

  const areUserControlsDisabled =
    isLoading || areRecordsAdding || areRecordsDeleting || !season?.areRosterAndStaffEditable;

  const handleUpdateRecord = async (data: any, record: any) =>
    updateRecord({
      entityId,
      seasonId,
      personId: record?.personId,
      dcPersonId: record?.dcPersonId,
      data,
      record,
    });
  const isViewOnly = viewOnly || !season?.areRosterAndStaffEditable;
  const imageClickHandler = isViewOnly ? undefined : (person: any) => setImageUploadPerson(person);

  const columns = useSeasonRosterColumns({
    viewOnly: isViewOnly,
    handlePersonImageClick: imageClickHandler,
    editMode,
    checkIfRecordBeingBusy,
    updateRecord: (data, record) => {
      runAsyncAction(handleUpdateRecord(data, record), "Failed to update season roster record");
    },
  });

  const addPeopleColumns = useAddPeopleRosterColumns({
    onAdd: (record: any) => {
      runAsyncAction(
        ensureRecordsBusy([record], async () =>
          addRecordsApi({
            entityId,
            seasonId,
            data: {
              players: [record],
            },
          })
        ),
        "Failed to add season roster record"
      );
    },
    onDelete: (record: any) => {
      runAsyncAction(
        ensureRecordsBusy([record], async () =>
          deleteRecordsApi({
            entityId,
            seasonId,
            data: getPersonDeleteApiData([record]),
          })
        ),
        "Failed to delete season roster record"
      );
    },
    checkIfRecordBeingBusy,
    getAlreadyAdded: (record: any) =>
      roster.find((x: any) => x.personId === record.personId && x.dcPersonId === record.dcPersonId),
  });

  const onDeleteSelectedRows = () => {
    runAsyncAction(
      ensureRecordsBusy(selectedRowRecords, async () =>
        deleteRecordsApi(
          {
            entityId,
            seasonId,
            data: getPersonDeleteApiData(selectedRowRecords),
          },
          {
            onSuccess: () => setSelectedRowRecords([]),
          }
        )
      ),
      "Failed to delete selected season roster records"
    );
  };

  const onEditModeChange = (value: boolean) => {
    setEditMode(value);
    if (!value) {
      setSelectedRowRecords([]);
    }
  };

  return (
    <>
      <UploadPersonImageModal
        person={imageUploadPerson}
        handleClose={() => setImageUploadPerson(undefined)}
        updatePerson={async (data) => {
          await handleUpdateRecord(data, imageUploadPerson);
          setImageUploadPerson({ ...imageUploadPerson, ...data });
        }}
      />
      <Row className="mb-10" align="middle">
        <Col span={8}>
          {selectedRowRecords.length > 0 && (
            <DeleteWithConfirmationButton
              disabled={areUserControlsDisabled}
              count={selectedRowRecords.length}
              onDelete={onDeleteSelectedRows}
            />
          )}
        </Col>
        <Col span={16} style={{ display: "flex", justifyContent: "flex-end" }}>
          <Space>
            {!viewOnly && (
              <>
                <EditModeToggle disabled={areUserControlsDisabled} onChange={onEditModeChange} />
                <CopyPeople
                  type={PeopleTypes.Roster}
                  disabled={areUserControlsDisabled}
                  entityId={entityId}
                  currentSeasonId={seasonId}
                />
                <AddPeople
                  disabled={areUserControlsDisabled}
                  entityId={entityId}
                  title={formatMessage({ id: "button.addPlayer" })}
                  columns={addPeopleColumns}
                />
              </>
            )}
            <SearchInput onSearch={setSearchText} />
          </Space>
        </Col>
      </Row>
      <Row>
        <RosterAndStaffTable
          isLoading={isLoading || areRecordsAdding || areRecordsDeleting}
          records={filteredRoster}
          columns={columns}
          rowSelection={
            (!viewOnly && editMode
              ? {
                  selectedRowKeys,
                  onChange: (_: any, rowRecords: Array<any>) => setSelectedRowRecords(rowRecords as any),
                }
              : null) as any
          }
        />
      </Row>
    </>
  );
};
